import React, { useEffect } from "react";
import type { FC, ReactNode } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { authenticated, impersonated } from "@containers/Auth/store/selectors";
import { setRedirectTo } from "@containers/Auth/store/actions";
import RetirementRedirect from "@containers/Auth/components/RetirementRedirect";
import { AuthFlow } from "@shared/interfaces";
import { checkIfSuperAdmin } from "@shared/utils/ACL";

import { NamesOfParentRoutes } from "../constants";

interface AuthGuardProps {
  children?: ReactNode;
}

const AuthGuard: FC<AuthGuardProps> = (props) => {
  const { children } = props;
  const isAuthenticated = useSelector(authenticated());
  const isImpersonated = useSelector(impersonated());
  const location = useLocation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const authFlow = searchParams.get("auth_flow") as AuthFlow;

  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(setRedirectTo(`${location.pathname}${location.search}`));
    }
  }, [isAuthenticated, dispatch, location.pathname, location.search, authFlow]);

  if (!isAuthenticated) {
    return <Navigate to={{ pathname: NamesOfParentRoutes.AUTH, search: location.search }} />;
  }
  // The DataFlik app is retired: block every non-superadmin account on the
  // retired host(s) and send them to the new application. Superadmins (internal
  // staff) keep full access, and impersonation sessions are left untouched so
  // staff can still impersonate regular users for support.
  if (!isImpersonated && !checkIfSuperAdmin()) {
    return <RetirementRedirect />;
  }

  if (authFlow === AuthFlow.OAuth) {
    return <Navigate to={{ pathname: NamesOfParentRoutes.OAUTH, search: location.search }} />;
  }

  return <>{children}</>;
};

export default AuthGuard;
